import { api } from './client';
import { deleteAdminMutationWithStatus, postAdminMutation } from './admin-mutation';
import {
  accountDeletionResult,
  accountDeletionResultFromError,
  type AccountDeletionResult,
} from './account-deletion';

export interface AdminMe {
  is_admin: boolean;
  login?: string | null;
}

export interface UsersBlock {
  total: number;
  new_today: number;
  new_week: number;
}

export interface WorkoutsBlock {
  total: number;
  today: number;
}

export interface SetsBlock {
  total: number;
}

export interface MealsBlock {
  total: number;
  today: number;
  measurements?: number;
}

export interface AIBlock {
  requests_24h: number;
  cache_hits_24h: number;
}

export interface SupplementsBlock {
  usage_records: number;
}

export interface AdminOverview {
  users: UsersBlock | null;
  workouts: WorkoutsBlock | null;
  sets: SetsBlock | null;
  meals: MealsBlock | null;
  ai: AIBlock | null;
  supplements: SupplementsBlock | null;
}

export interface AdminListResponse {
  env: string[];
  db: { id: number; login: string; added_by: string | null; added_at: string }[];
}

export interface AdminUser {
  id: number;
  login: string;
  fio: string;
  sex: boolean | null;
  birthday: string | null;
  age: number | null;
  height: number | null;
  weight: number | null;
  goal: number | null;
  is_blocked: boolean;
}

export interface AdminUsersResponse {
  items: AdminUser[];
  total: number | null;
  page: number;
  size: number;
  has_next: boolean;
}

export interface AdminUserDetail {
  profile: AdminUser;
  account_status: 'active' | 'blocked';
  consents: Record<string, { version: string | null; accepted_at: string | null }>;
  subscription: {
    plan: string;
    effective_plan: string;
    status: string;
    effective_status: string;
    period?: 'month' | 'year' | null;
    started_at?: string | null;
    expires_at?: string | null;
    provider?: string | null;
    days_remaining?: number | null;
    updated_at?: string | null;
  } | null;
  subscription_history: Array<{
    id: number;
    event_type: string;
    created_at: string;
    actor?: string | null;
    reason?: string | null;
  }>;
  counts: Record<'workouts' | 'meals' | 'measurements' | 'integrations', number | null>;
  last_activity_at: string | null;
  unavailable: string[];
}

export interface AdminAuditResponse {
  items: Array<{
    id: string;
    actor: string | null;
    target_user_id: number | null;
    target_login: string | null;
    action: string;
    request_id: string;
    outcome: string;
    created_at: string;
  }>;
  page: number;
  size: number;
  has_next: boolean;
  unavailable: string[];
}

export interface AdminServicesHealth {
  items: Array<{
    service: string;
    status: 'ok' | 'degraded' | 'unavailable';
    latency_ms: number | null;
    http_status: number | null;
  }>;
  checked_at: string;
}

export interface AdminErrorEvent {
  id: number;
  service: string;
  level: string;
  message: string;
  signature: string;
  user_id: number | null;
  created_at: string;
}

export interface AdminErrorsResponse {
  items: AdminErrorEvent[];
  total: number;
  page: number;
  size: number;
  has_next: boolean;
}

export type AdminDeletionStatus = 'not_started' | 'deleting' | 'failed' | 'deleted';

export interface AdminDeletionPreflight {
  user_id: number;
  login: string | null;
  allowed: boolean;
  confirmation: string | null;
  status: AdminDeletionStatus;
  blocked_reasons: Array<'self_delete' | 'environment_admin' | 'last_admin' | 'already_deleted'>;
  required_targets: string[];
  failed_targets: string[];
  request_id: string;
}

export interface AdminDeletionReceipt {
  user_id: number;
  status: AdminDeletionStatus;
  request_id: string;
  login: string | null;
  targets: Record<string, Record<string, unknown>>;
  failed_targets: string[];
}

export type AdminDeleteUserResult = AccountDeletionResult & AdminDeletionReceipt;

export interface AdminMutationReceipt {
  request_id: string;
  action: string;
  outcome: 'success';
  actor: string | null;
  target_user_id: number;
  event_id: number | null;
  created_at: string | null;
}

export interface AdminSubscriptionMutationResult {
  id: number;
  user_id: number;
  plan: 'free' | 'pro' | 'premium';
  status: string;
  started_at: string;
  expires_at: string | null;
  provider: string | null;
  effective_plan: 'free' | 'pro' | 'premium';
  effective_status: string;
  updated_at: string | null;
  audit_receipt: AdminMutationReceipt | null;
}

export interface AdminSubscriptionSummary {
  user_id: number;
  plan: 'free' | 'pro' | 'premium';
  status: string;
  period: 'month' | 'year' | null;
  started_at: string | null;
  expires_at: string | null;
  provider: string | null;
  effective_plan: 'free' | 'pro' | 'premium';
  effective_status: string;
  days_remaining: number | null;
  updated_at: string | null;
}

interface SubscriptionMutationBase {
  user_id: number;
  confirmation: string;
  reason: string;
  expected_plan: 'free' | 'pro' | 'premium';
  expected_updated_at?: string;
}

async function deleteUserWithReceipt(
  userId: number,
  confirmation: string,
  reason: string,
): Promise<AdminDeleteUserResult> {
  try {
    const response = await deleteAdminMutationWithStatus<AdminDeletionReceipt>(
      `/api/v1/admin/users/${userId}`,
      { confirmation, reason },
    );
    return { ...response.data, ...accountDeletionResult(response.status, response.data) };
  } catch (error) {
    const result = accountDeletionResultFromError(error);
    if (result) {
      const statusResult = await api.get<AdminDeletionReceipt>(
        `/api/v1/admin/users/${userId}/deletion-status`,
      ).then(response => response.data);
      return { ...statusResult, ...accountDeletionResult(200, statusResult) };
    }
    throw error;
  }
}

export const adminApi = {
  me: () => api.get<AdminMe>('/api/v1/admin/me').then(r => r.data),
  overview: () => api.get<AdminOverview>('/api/v1/admin/overview').then(r => r.data),
  listAdmins: () => api.get<AdminListResponse>('/api/v1/admin/admins').then(r => r.data),
  addAdmin: (login: string) => api.post<{ ok: boolean }>('/api/v1/admin/admins', { login }).then(r => r.data),
  removeAdmin: (login: string) => api.delete<{ ok: boolean }>(`/api/v1/admin/admins/${encodeURIComponent(login)}`).then(r => r.data),
  listUsers: (
    params: { page?: number; size?: number; search?: string; plan?: 'free' | 'pro' | 'premium'; account_status?: 'active' | 'blocked' } = {},
    signal?: AbortSignal,
  ) => api.get<AdminUsersResponse>('/api/v1/admin/users', { params, signal }).then(r => r.data),
  getUserDetail: (userId: number) =>
    api.get<AdminUserDetail>(`/api/v1/admin/users/${userId}/detail`).then(r => r.data),
  listSubscriptions: (userIds: number[]) =>
    api.post<{ items: AdminSubscriptionSummary[] }>(
      '/api/v1/billing/admin/subscriptions/query',
      { user_ids: userIds },
    ).then(r => r.data),
  getDeletionPreflight: (userId: number) =>
    api.get<AdminDeletionPreflight>(`/api/v1/admin/users/${userId}/deletion-preflight`).then(r => r.data),
  getDeletionStatus: (userId: number) =>
    api.get<AdminDeletionReceipt>(`/api/v1/admin/users/${userId}/deletion-status`).then(r => r.data),
  audit: (params: { page?: number; size?: number } = {}) =>
    api.get<AdminAuditResponse>('/api/v1/admin/audit', { params }).then(r => r.data),
  servicesHealth: () =>
    api.get<AdminServicesHealth>('/api/v1/admin/services/health').then(r => r.data),
  errors: (params: { page?: number; size?: number; service?: string; level?: string } = {}) =>
    api.get<AdminErrorsResponse>('/api/v1/admin/errors', { params }).then(r => r.data),
  grantSubscription: (body: SubscriptionMutationBase & {
    plan: 'pro' | 'premium';
    period?: 'month' | 'year';
    expires_at?: string;
  }) => postAdminMutation<AdminSubscriptionMutationResult>('/api/v1/billing/admin/confirmed/grant', body),
  extendSubscription: (body: SubscriptionMutationBase & { period: 'month' | 'year' }) =>
    postAdminMutation<AdminSubscriptionMutationResult>('/api/v1/billing/admin/confirmed/extend', body),
  setSubscriptionStatus: (body: SubscriptionMutationBase & { status: 'active' | 'paused' }) =>
    postAdminMutation<AdminSubscriptionMutationResult>('/api/v1/billing/admin/confirmed/status', body),
  revokeSubscription: (body: SubscriptionMutationBase) =>
    postAdminMutation<AdminSubscriptionMutationResult>('/api/v1/billing/admin/confirmed/revoke', body),
  deleteUserWithReceipt,
  deleteUser: async (
    userId: number,
    confirmation: string,
    reason: string,
  ): Promise<AccountDeletionResult> => {
    const result = await deleteUserWithReceipt(userId, confirmation, reason);
    return { phase: result.phase };
  },
};
